export type ParsedDecimal = {
  sign: 1 | -1;
  digits: bigint;
  scale: number;
};

function pow10(exp: number): bigint {
  let out = 1n;
  for (let i = 0; i < exp; i += 1) out *= 10n;
  return out;
}

export function parseDecimalString(value: string | null | undefined): ParsedDecimal | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!match) return null;

  const sign: 1 | -1 = match[1] === '-' ? -1 : 1;
  const whole = match[2] ?? '0';
  const frac = match[3] ?? '';
  const scale = frac.length;
  const digitsRaw = BigInt(`${whole}${frac}`);

  if (digitsRaw === 0n) {
    return { sign: 1, digits: 0n, scale };
  }

  return { sign, digits: digitsRaw, scale };
}

function alignToScale(parsed: ParsedDecimal, scale: number): bigint {
  const factor = pow10(scale - parsed.scale);
  const magnitude = parsed.digits * factor;
  return parsed.sign === -1 ? -magnitude : magnitude;
}

function formatMagnitude(value: bigint, scale: number, trimTrailingZeros: boolean): string {
  if (scale === 0) {
    return value.toString();
  }

  const base = value.toString().padStart(scale + 1, '0');
  const head = base.slice(0, -scale);
  const tail = trimTrailingZeros
    ? base.slice(-scale).replace(/0+$/, '')
    : base.slice(-scale);

  if (tail.length === 0) {
    return head;
  }

  return `${head}.${tail}`;
}

function formatScaledInteger(value: bigint, scale: number): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  return `${negative ? '-' : ''}${formatMagnitude(abs, scale, true)}`;
}

function formatParsedDecimal(parsed: ParsedDecimal, trimTrailingZeros: boolean): string {
  const body = formatMagnitude(parsed.digits, parsed.scale, trimTrailingZeros);
  const negative = parsed.sign === -1 && parsed.digits !== 0n;
  return `${negative ? '-' : ''}${body}`;
}

export function addDecimalStrings(current: string, next: string): string {
  const a = parseDecimalString(current);
  const b = parseDecimalString(next);
  if (!a || !b) return current;

  const scale = Math.max(a.scale, b.scale);
  const total = alignToScale(a, scale) + alignToScale(b, scale);
  return formatScaledInteger(total, scale);
}

/**
 * sumDecimalStrings — BigInt-backed sum of an array of BigDecimal wire strings.
 *
 * - Never coerces to Number / parseFloat.
 * - Empty array → "0".
 * - Null, undefined, empty-string, or em-dash ("—") values are treated as 0 (skipped).
 * - Mixed scales are handled correctly: the result scale is max(input scales).
 * - Handles negatives and large notionals without precision loss.
 */
export function sumDecimalStrings(values: readonly (string | null | undefined)[]): string {
  let total = 0n;
  let scale = 0;

  for (const raw of values) {
    if (raw === null || raw === undefined) continue;
    const trimmed = raw.trim();
    if (trimmed === '' || trimmed === '—') continue;

    const parsed = parseDecimalString(trimmed);
    if (!parsed) continue;

    if (parsed.scale > scale) {
      // Re-align the running total to the larger scale before adding.
      total *= pow10(parsed.scale - scale);
      scale = parsed.scale;
    }
    // Align this value to the running scale before accumulating.
    const factor = pow10(scale - parsed.scale);
    const magnitude = parsed.digits * factor;
    total += parsed.sign === -1 ? -magnitude : magnitude;
  }

  return formatScaledInteger(total, scale);
}

export function isDecimalNegative(value: string | null | undefined): boolean {
  const parsed = parseDecimalString(value);
  return Boolean(parsed && parsed.sign === -1 && parsed.digits !== 0n);
}

export function isDecimalZero(value: string | null | undefined): boolean {
  const parsed = parseDecimalString(value);
  return Boolean(parsed && parsed.digits === 0n);
}

export function compareAbsDecimals(a: string, b: string): number {
  const pa = parseDecimalString(a);
  const pb = parseDecimalString(b);
  if (!pa || !pb) return 0;

  const scale = Math.max(pa.scale, pb.scale);
  const av = pa.digits * pow10(scale - pa.scale);
  const bv = pb.digits * pow10(scale - pb.scale);
  if (av === bv) return 0;
  return av > bv ? 1 : -1;
}

export function compareDecimals(
  left: string | null | undefined,
  right: string | null | undefined,
): number | null {
  const a = parseDecimalString(left);
  const b = parseDecimalString(right);
  if (!a || !b) return null;

  if (a.digits === 0n && b.digits === 0n) {
    return 0;
  }

  if (a.sign !== b.sign) {
    return a.sign > b.sign ? 1 : -1;
  }

  const scale = Math.max(a.scale, b.scale);
  const av = alignToScale(a, scale);
  const bv = alignToScale(b, scale);
  if (av === bv) {
    return 0;
  }

  return av > bv ? 1 : -1;
}

export function shiftDecimalString(value: string | null | undefined, places: number): string | null {
  const parsed = parseDecimalString(value);
  if (!parsed) return null;

  if (places === 0 || parsed.digits === 0n) {
    return formatParsedDecimal(parsed, true);
  }

  if (places > 0) {
    if (parsed.scale >= places) {
      return formatParsedDecimal({ ...parsed, scale: parsed.scale - places }, true);
    }
    return formatParsedDecimal(
      {
        ...parsed,
        digits: parsed.digits * pow10(places - parsed.scale),
        scale: 0,
      },
      true,
    );
  }

  return formatParsedDecimal(
    {
      ...parsed,
      scale: parsed.scale + Math.abs(places),
    },
    true,
  );
}

export function formatDecimalFixed(
  value: string | null | undefined,
  fractionDigits: number,
  opts?: { signed?: boolean },
): string | null {
  const parsed = parseDecimalString(value);
  if (!parsed || fractionDigits < 0) {
    return null;
  }

  let digits = parsed.digits;
  if (parsed.scale > fractionDigits) {
    const factor = pow10(parsed.scale - fractionDigits);
    const whole = digits / factor;
    const remainder = digits % factor;
    digits = remainder * 2n >= factor ? whole + 1n : whole;
  } else if (parsed.scale < fractionDigits) {
    digits *= pow10(fractionDigits - parsed.scale);
  }

  const body = formatMagnitude(digits, fractionDigits, false);
  if (digits === 0n) {
    return body;
  }

  if (parsed.sign === -1) {
    return `-${body}`;
  }

  return opts?.signed ? `+${body}` : body;
}

/**
 * compareDecimalStrings — total ordering comparator for BigDecimal string values.
 *
 * Contract:
 * - Empty string, '—', null-ish (null / undefined) → treated as lowest possible value
 *   (sorts to the bottom on ascending, top on descending).
 * - Negative < zero < positive.
 * - For same sign: compares by magnitude using integer-digit length then lexically,
 *   then fractional digits (right-padded with zeros for equal length comparison).
 * - No Number() / parseFloat coercion — uses parseDecimalString (BigInt-backed).
 * - Returns negative / 0 / positive (standard comparator contract).
 */
export function compareDecimalStrings(a: string | null | undefined, b: string | null | undefined): number {
  const EMPTY_SENTINEL = -1;

  function isNullish(v: string | null | undefined): boolean {
    if (v === null || v === undefined) return true;
    const t = v.trim();
    return t === '' || t === '—'; // em dash '—'
  }

  const aNull = isNullish(a);
  const bNull = isNullish(b);
  if (aNull && bNull) return 0;
  if (aNull) return EMPTY_SENTINEL;
  if (bNull) return -EMPTY_SENTINEL;

  const result = compareDecimals(a, b);
  // compareDecimals returns null when either value fails to parse.
  // Treat unparseable as nullish (lowest).
  if (result === null) {
    const aOk = parseDecimalString(a) !== null;
    const bOk = parseDecimalString(b) !== null;
    if (!aOk && !bOk) return 0;
    if (!aOk) return EMPTY_SENTINEL;
    return -EMPTY_SENTINEL;
  }
  return result;
}

export function percentFromRatioDecimal(
  numerator: string | null | undefined,
  denominator: string | null | undefined,
): number {
  const num = parseDecimalString(numerator);
  const den = parseDecimalString(denominator);
  if (!num || !den || den.digits === 0n) return 0;

  const scale = Math.max(num.scale, den.scale);
  const n = num.digits * pow10(scale - num.scale);
  const d = den.digits * pow10(scale - den.scale);

  const bps = (n * 10000n) / d;
  const bounded = bps < 0n ? 0n : bps > 10000n ? 10000n : bps;
  return Number(bounded) / 100;
}
